/**
 * USA Jobs Finder block — faceted filtering over admin-ajax.
 *
 * Each facet is a dropdown of checkboxes with a search box (Figma 561:6482), so
 * a visitor can pick several terms in one facet. Terms OR-combine within a facet
 * and facets AND-combine. On any change the block re-queries the CPT and swaps
 * the results grid / count / pagination.
 */
export default function initUsaJobsFinder() {
  const instances = document.querySelectorAll('[data-ujf]');
  if (!instances.length) return;

  instances.forEach((root) => {
    const ajaxUrl = root.getAttribute('data-ajax-url');
    const nonce = root.getAttribute('data-nonce');
    const source = root.getAttribute('data-source') || 'usa_jobs';
    const perPage = parseInt(root.getAttribute('data-per-page'), 10) || 12;
    // Opaque to the client — round-tripped to the AJAX handler, which
    // re-validates every descriptor before querying.
    const prefilters = root.getAttribute('data-prefilters') || '';

    const filtersRoot = root.querySelector('[data-ujf-filters]');
    const facetEls = Array.from(root.querySelectorAll('.ujf__filter'));
    const checkboxes = Array.from(root.querySelectorAll('.ujf__option-checkbox'));
    const results = root.querySelector('[data-ujf-results]');
    const countEl = root.querySelector('[data-ujf-count]');
    const pagination = root.querySelector('[data-ujf-pagination]');
    const clearBtn = root.querySelector('[data-ujf-clear]');
    const pillsEl = root.querySelector('[data-ujf-pills]');
    const statusEl = root.querySelector('[data-ujf-status]');

    /**
     * Announce a result change. Clearing first forces a re-announce when the
     * message is identical to the last one (e.g. the same count twice).
     */
    const announce = (msg) => {
      if (!statusEl) return;
      statusEl.textContent = '';
      setTimeout(() => {
        statusEl.textContent = msg;
      }, 100);
    };

    if (!results) return;

    let page = 1;
    let requestId = 0;

    // taxonomy => [slug, …] for every checked box (multi-select).
    const getFilters = () => {
      const filters = {};
      checkboxes.forEach((cb) => {
        if (!cb.checked) return;
        const tax = cb.getAttribute('data-taxonomy');
        if (!tax) return;
        (filters[tax] = filters[tax] || []).push(cb.value);
      });
      return filters;
    };

    const anyChecked = () => checkboxes.some((cb) => cb.checked);

    const toggleClear = () => {
      if (!clearBtn) return;
      clearBtn.hidden = !anyChecked();
    };

    // Each facet toggle shows how many of its terms are selected.
    const updateToggles = () => {
      facetEls.forEach((facet) => {
        const count = facet.querySelectorAll('.ujf__option-checkbox:checked').length;
        const badge = facet.querySelector('[data-ujf-count-badge]');
        if (badge) {
          badge.hidden = count === 0;
          badge.textContent = count ? String(count) : '';
        }
        facet.classList.toggle('--has-selection', count > 0);
      });
    };

    // One removable pill per checked term.
    const renderPills = () => {
      if (!pillsEl) return;
      pillsEl.innerHTML = '';
      checkboxes.forEach((cb) => {
        if (!cb.checked) return;
        const tax = cb.getAttribute('data-taxonomy');
        const option = cb.closest('.ujf__option');
        const label =
          (option && option.querySelector('.ujf__option-text') && option.querySelector('.ujf__option-text').textContent) ||
          cb.value;
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'ujf__pill';
        pill.setAttribute('data-taxonomy', tax);
        pill.setAttribute('data-slug', cb.value);
        pill.setAttribute('aria-label', `Remove filter: ${label}`);
        const text = document.createElement('span');
        text.className = 'ujf__pill-label';
        text.textContent = label;
        // Empty span — the Material close icon is drawn by CSS as a
        // currentcolor mask (see __pill-x in the block SCSS).
        const x = document.createElement('span');
        x.className = 'ujf__pill-x';
        x.setAttribute('aria-hidden', 'true');
        pill.appendChild(text);
        pill.appendChild(x);
        pillsEl.appendChild(pill);
      });
    };

    // ── Dropdown open / close ────────────────────────────────────────────────
    const closeAllDropdowns = (except) => {
      facetEls.forEach((facet) => {
        if (facet === except) return;
        const dd = facet.querySelector('[data-ujf-dropdown]');
        const toggle = facet.querySelector('[data-ujf-toggle]');
        if (dd) dd.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        facet.classList.remove('--open');
      });
    };

    facetEls.forEach((facet) => {
      const toggle = facet.querySelector('[data-ujf-toggle]');
      const dd = facet.querySelector('[data-ujf-dropdown]');
      const search = facet.querySelector('[data-ujf-search]');
      const options = Array.from(facet.querySelectorAll('[data-ujf-option]'));

      if (toggle && dd) {
        toggle.addEventListener('click', () => {
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          closeAllDropdowns(facet);
          if (isOpen) {
            dd.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
            facet.classList.remove('--open');
          } else {
            dd.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
            facet.classList.add('--open');
            if (search) search.focus();
          }
        });
      }

      // Search filters the visible options by label substring.
      if (search) {
        search.addEventListener('input', () => {
          const q = search.value.trim().toLowerCase();
          options.forEach((opt) => {
            const label = opt.getAttribute('data-label') || '';
            opt.hidden = q !== '' && label.indexOf(q) === -1;
          });
        });
      }
    });

    // Close on outside click / Escape.
    document.addEventListener('click', (e) => {
      if (filtersRoot && !filtersRoot.contains(e.target)) closeAllDropdowns(null);
    });
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllDropdowns(null);
    });

    // Windowed pagination per the design: « ‹ 1 2 3 … 99 100 › »
    const buildPagination = (totalPages, current) => {
      if (!pagination) return;
      pagination.innerHTML = '';
      if (totalPages <= 1) return;

      const mkBtn = (label, targetPage, opts = {}) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ujf__page gg-pagination__page';
        if (opts.current) btn.classList.add('--current');
        if (opts.disabled) btn.disabled = true;
        if (opts.rel) btn.classList.add(opts.rel);
        btn.textContent = label;
        btn.setAttribute('data-page', targetPage);
        if (opts.current) btn.setAttribute('aria-current', 'page');
        if (opts.aria) btn.setAttribute('aria-label', opts.aria);
        return btn;
      };

      const mkEllipsis = () => {
        const span = document.createElement('span');
        span.className = 'gg-pagination__ellipsis';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = '…';
        return span;
      };

      pagination.appendChild(
        mkBtn('«', 1, { rel: '--first', disabled: current <= 1, aria: 'First page' }),
      );
      pagination.appendChild(
        mkBtn('‹', current - 1, { rel: '--prev', disabled: current <= 1, aria: 'Previous page' }),
      );

      const HEAD = 3; // always shown at the start
      const TAIL = 2; // always shown at the end
      const AROUND = 1; // either side of the current page, once it leaves them

      const wanted = new Set();
      for (let i = 1; i <= Math.min(HEAD, totalPages); i += 1) wanted.add(i);
      for (let i = Math.max(1, totalPages - TAIL + 1); i <= totalPages; i += 1) wanted.add(i);
      for (let i = current - AROUND; i <= current + AROUND; i += 1) {
        if (i >= 1 && i <= totalPages) wanted.add(i);
      }

      let previous = 0;
      [...wanted]
        .sort((a, b) => a - b)
        .forEach((pageNum) => {
          if (pageNum - previous > 1) pagination.appendChild(mkEllipsis());
          pagination.appendChild(mkBtn(String(pageNum), pageNum, { current: pageNum === current }));
          previous = pageNum;
        });

      pagination.appendChild(
        mkBtn('›', current + 1, { rel: '--next', disabled: current >= totalPages, aria: 'Next page' }),
      );
      pagination.appendChild(
        mkBtn('»', totalPages, { rel: '--last', disabled: current >= totalPages, aria: 'Last page' }),
      );
    };

    /**
     * @param {Function} [restoreFocus] Runs once the DOM has been rebuilt, to
     *   hand focus to a replacement for whatever the interaction destroyed.
     */
    const fetchResults = (restoreFocus) => {
      // Latest request wins. An isLoading gate here used to silently DROP a
      // filter change made while a fetch was in flight — the UI showed the new
      // value but the results, count and pills stayed stale.
      const thisRequest = ++requestId;
      results.setAttribute('aria-busy', 'true');
      root.classList.add('--loading');

      const filters = getFilters();
      toggleClear();
      updateToggles();
      renderPills();

      const body = new URLSearchParams();
      body.append('action', 'usa_jobs_finder');
      body.append('nonce', nonce);
      body.append('source', source);
      body.append('page', String(page));
      body.append('per_page', String(perPage));
      if (prefilters) {
        body.append('prefilters', prefilters);
      }
      Object.entries(filters).forEach(([tax, slugs]) => {
        slugs.forEach((slug) => body.append(`filters[${tax}][]`, slug));
      });

      fetch(ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        credentials: 'same-origin',
      })
        .then((res) => res.json())
        .then((json) => {
          if (thisRequest !== requestId) return; // superseded by a newer query
          if (!json || !json.success) throw new Error('Bad response');
          const data = json.data;
          results.innerHTML = data.cards;
          if (countEl) countEl.textContent = data.total;
          const totalPages = parseInt(data.total_pages, 10) || 0;
          buildPagination(totalPages, page);

          const n = parseInt(data.total, 10) || 0;
          if (n === 0) {
            announce('No opportunities match your filters.');
          } else {
            announce(
              `${n} result${n === 1 ? '' : 's'}` +
                (totalPages > 1 ? `, showing page ${data.page} of ${totalPages}.` : '.'),
            );
          }
        })
        .catch(() => {
          if (thisRequest !== requestId) return;
          results.innerHTML =
            '<p class="ujf__empty">Something went wrong loading results. Please try again.</p>';
          announce('Something went wrong loading results. Please try again.');
        })
        .finally(() => {
          if (thisRequest !== requestId) return; // a newer query owns the UI state
          results.setAttribute('aria-busy', 'false');
          root.classList.remove('--loading');
          // After the rebuild, so the replacement element exists to focus.
          if (typeof restoreFocus === 'function') restoreFocus();
        });
    };

    // Facet change → reset to page 1 and re-query.
    checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        page = 1;
        fetchResults();
      });
    });

    // Pagination (delegated). Rebuilding the pagination destroys the button
    // that was clicked, so focus lands on <body> — and scrollIntoView has
    // meanwhile moved the viewport away from where the visitor was.
    if (pagination) {
      pagination.addEventListener('click', (e) => {
        const btn = e.target.closest('.ujf__page');
        if (!btn || btn.disabled) return;
        const target = parseInt(btn.getAttribute('data-page'), 10);
        if (!target || target === page) return;
        page = target;
        fetchResults(() => {
          const same = pagination.querySelector(`.ujf__page[data-page="${target}"]:not([disabled])`);
          (same || pagination.querySelector('.ujf__page:not([disabled])'))?.focus({ preventScroll: true });
        });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // Clear all. The button hides itself once the filters are gone, so move
    // focus to the first facet before it disappears from under the visitor.
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        checkboxes.forEach((cb) => {
          cb.checked = false;
        });
        page = 1;
        const firstToggle = facetEls[0] && facetEls[0].querySelector('[data-ujf-toggle]');
        if (firstToggle) firstToggle.focus();
        fetchResults();
      });
    }

    // Remove a single term via its pill. renderPills() wipes the pill that was
    // clicked, so hand focus to that facet's toggle first.
    if (pillsEl) {
      pillsEl.addEventListener('click', (e) => {
        const pill = e.target.closest('.ujf__pill');
        if (!pill) return;
        const tax = pill.getAttribute('data-taxonomy');
        const slug = pill.getAttribute('data-slug');
        const cb = checkboxes.find(
          (c) => c.getAttribute('data-taxonomy') === tax && c.value === slug,
        );
        if (cb) cb.checked = false;
        const facet = cb && cb.closest('.ujf__filter');
        const toggle = facet && facet.querySelector('[data-ujf-toggle]');
        if (toggle) toggle.focus();
        page = 1;
        fetchResults();
      });
    }

    // Build initial pagination from the server-rendered state.
    buildPagination(
      parseInt(pagination?.getAttribute('data-total-pages'), 10) || 0,
      parseInt(pagination?.getAttribute('data-current'), 10) || 1,
    );
  });
}
